// Cross-tree sync signals (offline-first Chunk 2). The rule-#26 window-event
// indirection lets sync.ts (the WRITER) and the enrichment hooks (the READERS)
// share the sequencing + cloud-account signals without an import cycle: a
// direct sync.ts -> enrichment -> store -> sync chain would cycle. The ONLY
// import here is `@/lib/db` (for the durable cloud-account sentinel), an
// acyclic leaf — db.ts imports no sync / store / enrichment module.
//
// See docs/plans/offline-first-2026-06-13.md §3.1b.
import * as db from '@/lib/db';

export const SYNC_SETTLED_EVENT = 'adia:sync-settled';
export const ENRICHMENT_RECHECK_EVENT = 'adia:enrichment-recheck';

const CLOUD_ACCOUNT_KEY = 'adia-cloud-account';
const LAST_SYNC_KEY = 'adia-last-sync';
const DEFAULT_LAST_SYNC = '1970-01-01T00:00:00.000Z';

// --- Signed-in-pull-scoped STICKY latch ---------------------------------
//
// `hasSyncSettled()` === "a SIGNED-IN pull has settled this page-session and
// has not been invalidated by a later reconnect / sign-in / sign-out." A
// module-level boolean, so a settle that happens BEFORE the enrichment hooks
// mount (initOutbox runs before React renders) is still observed at first run
// (the pass-13 sticky fix). `dispatchSyncSettled()` sets it true BEFORE
// firing the event for exactly that reason.

let settled = false;

export function dispatchSyncSettled(): void {
  settled = true;
  window.dispatchEvent(new CustomEvent(SYNC_SETTLED_EVENT));
}

export function hasSyncSettled(): boolean {
  return settled;
}

export function resetSyncSettled(): void {
  settled = false;
}

// --- Per-device cloud-account signal ------------------------------------
//
// `hasCloudAccount()` === "this device has EVER had a cloud account (so a
// local row MIGHT have a cloud counterpart, including via the lost-mark gap),
// OR we do not yet KNOW the account state." It is conservative-true until
// `accountKnown()` (authResolved && bootSeedDone), so it can never read false
// on a configured deployment in the cold-boot window (pass-16). The DURABLE
// source of truth is the IDB app-meta sentinel (db.getCloudAccountFlag);
// localStorage is only a fast synchronous cache + a legacy upgrade bridge.

let cloudAccount = false;
let authResolved = false;
let bootSeedDone = false;
// Set true once the durable sentinel write has confirmed at least once this
// session. Until then markCloudAccount() re-fires the write so a transient
// rejection self-heals (codex pass-19).
let sentinelConfirmed = false;

/**
 * The best-effort legacy localStorage signal: an explicit cloud-account cache
 * flag, OR a present-non-default last-sync timestamp (proof the device has
 * synced before). Read at module init to seed the in-memory flag, and by the
 * boot seed (sync.ts runBootSeed) to migrate the signal into the durable IDB
 * sentinel. Monotonic — only ever indicates TRUE.
 */
export function hasLegacyCloudAccountSignal(): boolean {
  try {
    if (localStorage.getItem(CLOUD_ACCOUNT_KEY) === '1') return true;
    const ls = localStorage.getItem(LAST_SYNC_KEY);
    if (ls != null && ls !== '' && ls !== DEFAULT_LAST_SYNC) return true;
  } catch {
    // private-mode Safari throws on access — treat as no signal.
  }
  return false;
}

// Module init: synchronously seed the in-memory flag from the localStorage
// cache so a returning device is covered before the async boot seed completes.
if (hasLegacyCloudAccountSignal()) {
  cloudAccount = true;
}

function accountKnown(): boolean {
  return authResolved && bootSeedDone;
}

export function hasCloudAccount(): boolean {
  return cloudAccount || !accountKnown();
}

/**
 * Record that this device has a cloud account. Called on SESSION OBSERVATION
 * — a session-bearing auth event and a successful pushPrayer — both moments a
 * cloud counterpart provably exists, recorded independently of any per-row
 * syncedAt. Sets the in-memory flag SYNCHRONOUSLY (authoritative for the
 * session, so a rejected durable write never makes hasCloudAccount() read
 * false this session), then writes the localStorage cache and re-fires the
 * durable IDB sentinel write until one confirms. NEVER clears.
 */
export function markCloudAccount(): void {
  cloudAccount = true;
  try {
    localStorage.setItem(CLOUD_ACCOUNT_KEY, '1');
  } catch {
    // ignore — the in-memory flag is already authoritative this session.
  }
  if (!sentinelConfirmed) {
    // The .catch is REQUIRED (codex pass-20): the durable write can reject
    // (transient/forced IDB failure, exercised in §3.6) and that expected
    // rejection must not surface as an unhandled promise rejection.
    // sentinelConfirmed stays false on reject so the next call re-fires.
    void db
      .setCloudAccountFlag()
      .then(() => {
        sentinelConfirmed = true;
      })
      .catch(() => {});
  }
}

/**
 * Flip authResolved true on the first auth event (or synchronously at
 * initOutbox when Supabase is unconfigured — no auth system to wait on). Also
 * dispatches a lightweight recheck so the enrichment workers re-evaluate the
 * per-device rule the moment account state can become known. Idempotent.
 */
export function markAuthResolved(): void {
  if (authResolved) return;
  authResolved = true;
  window.dispatchEvent(new CustomEvent(ENRICHMENT_RECHECK_EVENT));
}

/**
 * Flip bootSeedDone true after the boot seed settles CONCLUSIVELY (sync.ts
 * runBootSeed). Dispatches the recheck so a never-account device's
 * boot-deferred rows enrich the moment accountKnown() resolves, without
 * waiting for an incidental online/visibility/signature change. Idempotent —
 * NOT dispatchSyncSettled (the recheck must not open the signed-in latch).
 */
export function markBootSeedDone(): void {
  if (bootSeedDone) return;
  bootSeedDone = true;
  window.dispatchEvent(new CustomEvent(ENRICHMENT_RECHECK_EVENT));
}
